/**
 * Schedule threshold helpers — DEV-1080, Chunk B (Skip's 2026-05-22 call).
 *
 * The Mini-Project Schedule KPI classifies the gap between planned and
 * actual percent. The default rule of "within 5pp green, 5–15pp yellow,
 * over 15pp red" works fine for typical multi-month projects but is too
 * tight for very short projects (a 10-day project moves ~10pp per day,
 * so a 5pp gap might just mean "half a day late on a one-time task").
 *
 * Auto-scale rule (Skip's decision over Lead-settable + disclosed
 * alternatives):
 *
 *   - Green: gap ≤ max(5pp, 1 day of expected progress)
 *   - Yellow: gap ≤ max(15pp, 1 week of expected progress)
 *   - Red: anything past the yellow ceiling
 *
 * Floor preserves the platform-wide minimum standard. Long projects
 * keep the conventional 5/15. Short projects widen automatically.
 *
 * Playbooks can hardcode their own thresholds in the future by passing
 * a fixed ScheduleThresholds object directly; this module always
 * provides the system default when no playbook override is supplied.
 *
 * THIS IS THE CANONICAL SCHEDULE-CLASSIFIER. Any surface that wants to
 * map a (planned, actual) pair to a color band MUST import from here.
 * The inline classifyGap functions in StatusWriter and ScheduleModal
 * have been retired in favor of this.
 */

export interface ScheduleThresholds {
  /** Maximum gap (percentage points) that still counts as green. */
  greenMaxPct: number;
  /** Maximum gap that still counts as yellow. Anything past this is red. */
  yellowMaxPct: number;
}

/** Static defaults — applied as the floor for all projects, and as the
 *  fallback when project duration isn't known. */
export const DEFAULT_SCHEDULE_THRESHOLDS: ScheduleThresholds = {
  greenMaxPct: 5,
  yellowMaxPct: 15,
};

/** Compute scaled thresholds for a project of the given duration.
 *  Pass null when the project has no start/end dates set — falls back
 *  to the static defaults. */
export function computeScheduleThresholds(
  durationDays: number | null,
): ScheduleThresholds {
  if (!durationDays || durationDays <= 0) {
    return DEFAULT_SCHEDULE_THRESHOLDS;
  }
  const expectedPerDay = 100 / durationDays;
  return {
    // 1 day of expected progress — floored at the default 5pp.
    greenMaxPct: Math.max(DEFAULT_SCHEDULE_THRESHOLDS.greenMaxPct, expectedPerDay),
    // 1 week of expected progress — floored at the default 15pp.
    yellowMaxPct: Math.max(
      DEFAULT_SCHEDULE_THRESHOLDS.yellowMaxPct,
      expectedPerDay * 7,
    ),
  };
}

/** Days between two ISO date strings (YYYY-MM-DD or full ISO). Returns
 *  null if either date is missing or end is not after start. */
export function projectDurationDays(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): number | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  return (end - start) / 86400000;
}

export type ScheduleGapColor = "success" | "warning" | "danger";

/** Classify a planned/actual gap into a color band + human-readable
 *  label ("3 pt behind" / "on track" / "2 pt ahead").
 *
 *  DEV-1117 (Skip 2026-05-26): "ahead" is doing better than plan, not a
 *  deviation that needs attention. Color signals SEVERITY OF BEING
 *  BEHIND only — any gap ≤ 0 short-circuits to green.
 *  Threshold bands apply only to positive (behind-plan) gaps:
 *    - 0…greenMaxPct       → green
 *    - greenMaxPct…yellowMaxPct → yellow
 *    - > yellowMaxPct      → red */
export function classifyScheduleGap(
  plannedPct: number,
  actualPct: number,
  thresholds: ScheduleThresholds = DEFAULT_SCHEDULE_THRESHOLDS,
): { color: ScheduleGapColor; label: string } {
  const gap = plannedPct - actualPct;

  // Ahead-of-plan or exactly on track → always green. Label still
  // reflects direction so the user sees "5 pt ahead" rather than the
  // ambiguous "on track".
  if (gap <= 0) {
    return {
      color: "success",
      label: gap === 0 ? "on track" : `${Math.abs(gap).toFixed(0)} pt ahead`,
    };
  }

  // Behind plan — graduated thresholds.
  let color: ScheduleGapColor;
  if (gap <= thresholds.greenMaxPct) color = "success";
  else if (gap <= thresholds.yellowMaxPct) color = "warning";
  else color = "danger";

  return { color, label: `${gap.toFixed(0)} pt behind` };
}

/** Render the threshold band as a human-readable legend string. Used
 *  by the writer and modal so the displayed numbers stay in sync with
 *  the rule the classifier is actually applying.
 *
 *  DEV-1117: Legend reflects the asymmetric rule — being ahead is
 *  always green; only being behind triggers warning/danger. Previous
 *  legend ("within 5 pts green · 5–15 pts yellow") was direction-
 *  ambiguous and reinforced the symmetric-classification bug. */
export function formatThresholdLegend(thresholds: ScheduleThresholds): string {
  return `ahead or within ${thresholds.greenMaxPct.toFixed(0)} pts behind green · ${thresholds.greenMaxPct.toFixed(0)}–${thresholds.yellowMaxPct.toFixed(0)} pts behind yellow · ${thresholds.yellowMaxPct.toFixed(0)}+ pts behind or past deadline red`;
}
